import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useGlobalState } from "@/store/commonStore";
import type { AppDispatch, RootState } from "@/features/redux/store";
import { enqueue } from "./modalOrchestratorSlice";
import {
  isLoginReminderDismissedForCurrentHomeEntry,
  isLoginReminderHomePage,
  LOGIN_REMINDER_DEDUPE_KEY,
  LOGIN_REMINDER_DISMISSED_EVENT,
  markLoginReminderDismissedForCurrentHomeEntry,
  resetLoginReminderDismissedForCurrentHomeEntry,
  shouldEnqueueLoginReminder,
} from "./loginReminder";

export const useLoginReminderPopup = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { isLogin } = useGlobalState();
  const dismissedOnCurrentHomeEntryRef = useRef(false);
  const wasOnHomeRef = useRef(isLoginReminderHomePage(location.pathname));
  const prevIsLoginRef = useRef(isLogin);

  const hasPendingLoginReminder = useSelector((state: RootState) => {
    const { queue, deferredGating, currentTask } = state.modalOrchestrator;
    return (
      currentTask?.type === "login_remind" ||
      queue.some((task) => task.type === "login_remind") ||
      deferredGating.some((task) => task.type === "login_remind")
    );
  });

  useEffect(() => {
    const onHome = isLoginReminderHomePage(location.pathname);

    if (!onHome && wasOnHomeRef.current) {
      dismissedOnCurrentHomeEntryRef.current = false;
      resetLoginReminderDismissedForCurrentHomeEntry();
    }

    if (isLogin || (prevIsLoginRef.current && !isLogin)) {
      dismissedOnCurrentHomeEntryRef.current = false;
      resetLoginReminderDismissedForCurrentHomeEntry();
    }

    wasOnHomeRef.current = onHome;
    prevIsLoginRef.current = isLogin;
  }, [isLogin, location.pathname]);

  useEffect(() => {
    const handleDismissed = () => {
      dismissedOnCurrentHomeEntryRef.current = true;
      markLoginReminderDismissedForCurrentHomeEntry();
    };

    window.addEventListener(LOGIN_REMINDER_DISMISSED_EVENT, handleDismissed);
    return () => {
      window.removeEventListener(
        LOGIN_REMINDER_DISMISSED_EVENT,
        handleDismissed,
      );
    };
  }, []);

  useEffect(() => {
    if (
      !shouldEnqueueLoginReminder({
        isLogin,
        pathname: location.pathname,
        dismissedOnCurrentHomeEntry:
          dismissedOnCurrentHomeEntryRef.current ||
          isLoginReminderDismissedForCurrentHomeEntry(),
        hasPendingLoginReminder,
      })
    ) {
      return;
    }

    dispatch(
      enqueue({
        id: LOGIN_REMINDER_DEDUPE_KEY,
        type: "login_remind",
        priority: 20,
        dedupeKey: LOGIN_REMINDER_DEDUPE_KEY,
        payload: {},
      }),
    );
  }, [dispatch, hasPendingLoginReminder, isLogin, location.pathname]);
};

export default useLoginReminderPopup;
